import React from "react";
import styled, { useTheme } from "styled-components";
import Modal from "common/Modal/Modal";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import Empty from "common/Empty/Empty";
import IconWithFallback from "Global/IconWithFallback";
import { ICON_TYPES } from "@shared/common/constants";
import RequiredFieldMessage, {
  Position,
} from "common/Animations/RequiredFieldMessage";
import Link from "common/Typography/Link";
import Button from "common/Buttons/Button";
import Text from "common/Typography/Text";
import Flex from "common/Boxes/Flex";
import useWatchlistModal from "features/watchlist/common/useWatchlistModal";
import Checkbox from "common/Checkbox/Checkbox";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import BottomDrawer from "common/Drawers/BottomDrawer.tsx";
import { useWindowSize } from "react-use";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { phoneMaxWidth } from "common/NewLook/layout.ts";

interface AddToWatchlistModalProps {
  symbol?: string;
  visible: boolean;
  onClose: () => void;
}

const AddToWatchlistModal = ({
  symbol,
  visible,
  onClose,
}: AddToWatchlistModalProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.WATCHLIST);
  const appTheme = useTheme();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { height } = useWindowSize();

  const {
    getExchangeToSymbolsStatus,
    setValueChanged,
    selectedExchanges,
    setSelectedExchanges,
    isExchangesEmptyError,
    setIsExchangesEmptyError,
    showEmpty,
    showExchangeList,
    onCloseModal,
    getOkButtonText,
    updateWatchlistHandler,
    isLoading,
    clearAll,
    selectAll,
    sortedExchanges,
    numSelectedExchanges,
  } = useWatchlistModal(symbol, onClose, visible);

  const exchangesList = (
    <Checkbox.Group
      value={selectedExchanges}
      onChange={(exchanges) => {
        setValueChanged(true);
        setSelectedExchanges(exchanges as string[]);
        setIsExchangesEmptyError(false);
      }}
      style={{
        display: "flex",
        flexDirection: "column",
        maxHeight: isBottomNavigator ? height * 0.5 : undefined,
        overflowY: "auto",
        flexWrap: "nowrap",
        padding: isBottomNavigator ? "0 16px" : 0,
      }}
    >
      {sortedExchanges.map((exchange, index) => (
        <StyledCheckbox value={exchange} key={index}>
          <IconExchangeWrapper>
            <IconWithFallback
              coinOrExchangeName={exchange}
              iconStyle={{ height: 20, width: 20 }}
              iconType={ICON_TYPES.EXCHANGE}
            />
            <Exchange>{exchange}</Exchange>
          </IconExchangeWrapper>
        </StyledCheckbox>
      ))}
    </Checkbox.Group>
  );

  const buttons = (
    <div style={{ padding: isBottomNavigator ? "8px 12px 12px" : 0 }}>
      <RequiredFieldMessage
        requirement={isExchangesEmptyError}
        errorMessage={t("modal.add.error_message")}
        position={Position.TOP}
        style={{
          fontSize: appTheme.typography.fontSizes.S,
          marginBottom: 4,
          textAlign: "end",
        }}
      />
      <ButtonsWrapper>
        <Link onClick={onCloseModal}>{t("modal.add.cancel")}</Link>
        <Button
          type="primary"
          loading={isLoading}
          onClick={updateWatchlistHandler}
        >
          {getOkButtonText()}
        </Button>
      </ButtonsWrapper>
    </div>
  );

  const topSection = (
    <TopSectionWrapper>
      <Text type="secondary">{symbol}</Text>
      <NumSelected>
        {numSelectedExchanges || 0} {t("modal.add.selected")}
      </NumSelected>
      <Link onClick={!numSelectedExchanges ? selectAll : clearAll}>
        {!numSelectedExchanges
          ? t("modal.add.select_all")
          : t("modal.add.clear_all")}
      </Link>
    </TopSectionWrapper>
  );

  const content = (
    <>
      {showEmpty && <StyledEmpty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
      {showExchangeList && (
        <>
          {topSection}
          {exchangesList}
          {buttons}
        </>
      )}
    </>
  );

  if (isBottomNavigator) {
    return (
      <BottomDrawer
        title={t("modal.add.title")}
        open={visible}
        onClose={onCloseModal}
        content={content}
        contentStyle={{ padding: 0 }}
      />
    );
  }

  return (
    <Modal
      title={t("modal.add.title")}
      open={visible}
      onCancel={onCloseModal}
      footer={false}
      destroyOnHidden={true}
    >
      <LoadingSpinnerOverlay
        isLoading={getExchangeToSymbolsStatus.isFetching}
        spinnerSize="default"
        spinnerMarginTop={100}
        wrapStyle={{ minHeight: 80 }}
      >
        {content}
      </LoadingSpinnerOverlay>
    </Modal>
  );
};

export default AddToWatchlistModal;

const TopSectionWrapper = styled(Flex)`
  justify-content: flex-start;
  margin-bottom: ${(p) => (p.theme.isNewLook ? 20 : 24)}px;
  @media (max-width: ${phoneMaxWidth}) {
    padding: 16px 16px 0;
  }
`;
const NumSelected = styled(Text)`
  border-left: ${(p) => p.theme.borders.medium};
  border-right: ${(p) => p.theme.borders.medium};
  margin: 0 24px;
  padding: 0 24px;
  ${(p) =>
    p.theme.isNewLook &&
    `
    margin: 0 12px;
    padding: 0 12px;
    border-color: ${p.theme.colors.lightBorderGrey};
    color: ${p.theme.colors.secondaryText};
    font-variant-numeric: tabular-nums;
  `}
`;
const IconExchangeWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-left: 4px;
`;
const StyledCheckbox = styled(Checkbox)`
  &.ant-checkbox-wrapper {
    margin-left: 0;
  }
  .ant-checkbox {
    top: 0;
  }
  &:not(:last-child) {
    margin-bottom: 16px;
  }
  display: flex;
  align-items: center;
`;
const Exchange = styled(Text)`
  margin-left: 8px;
`;
const StyledEmpty = styled(Empty)`
  padding: 5px 0;
`;
const ButtonsWrapper = styled(Flex)`
  justify-content: flex-end;
  grid-gap: 16px;
`;
